(() => {
  const ELEMENT_NAME = 'store-reservation-o2';

  if (customElements.get(ELEMENT_NAME)) return;

  const SELECTORS = {
    launcher: 'store-reservation-o2',
    flow: 'reservation-flow-o2',
    drawer: 'pickup-availability-drawer[data-pickup-availability-o2]',
    locations: '[data-pickup-availability-location]',
    list: '[data-store-availability-drawer-content]',
    search: '[data-pickup-availability-search]',
    form: '[data-reservation-form]',
    step: '[data-reservation-step]',
    button: 'button',
    productInfo: 'product-info',
    productTitle: '[data-reservation-product-title]',
    productImage: 'img.reservation-o2__product-image',
    productMedia: '[data-reservation-product-media]',
    productPlaceholder: '[data-reservation-product-placeholder]',
    benefits: '[data-reservation-benefits]',
    availabilityNotice: '[data-reservation-availability-notice]',
    pickupOpen: '[data-pickup-availability-open]',
    customer: '[data-reservation-customer]',
    copy: '[data-reservation-copy]',
    open: '[data-reservation-open]',
    fallback: '[data-reservation-fallback]',
    selectStore: '[data-reservation-select]',
    back: '[data-reservation-back]',
    changeStore: '[data-reservation-change], [data-reservation-choose]',
    changeSelectedStore: '[data-reservation-change]',
    retry: '[data-reservation-retry]',
    invalidField: '[aria-invalid="true"]',
    heading: '#PickupAvailabilityHeadingO2',
    body: '.reservation-o2__body',
    alternatives: '[data-reservation-alternatives]',
    count: '[data-reservation-count]',
    validationError: '[data-reservation-validation-error]',
    product: '[data-reservation-product]',
    formActions: '[data-reservation-back], [data-reservation-submit]',
    chooseStore: '[data-reservation-choose]',
    close: '[data-reservation-close]',
    subtitle: '[data-reservation-subtitle]',
    storeName: '[data-reservation-store-name]',
    storeAddress: '[data-reservation-store-address]',
    number: '[data-reservation-number]',
    sms: '[data-reservation-sms]',
    purchase: '[data-reservation-purchase]',
    errorTitle: '[data-reservation-error-title]',
    errorBody: '[data-reservation-error-body]',
    empty: '[data-reservation-empty]',
    refreshing: '[data-reservation-refreshing]',
    refreshErrors: '[data-reservation-refresh-error], [data-reservation-list-error], [data-reservation-retry]',
    errorEmpty: '[data-reservation-error-empty]',
    refreshError: '[data-reservation-refresh-error]',
    listError: '[data-reservation-list-error]',
    unavailableActions: '[data-reservation-choose], [data-reservation-error-empty]',
    field: (name) => `[name="${name}"]`,
    fieldError: (name) => `[data-reservation-field-error="${name}"]`,
  };
  const EVENTS = { opening: 'pickup:opening', closed: 'pickup:closed', filtered: 'pickup:filtered' };
  const CLASSES = { productImage: 'reservation-o2__product-image' };

  const FIELDS = ['phoneNumber', 'customerEmail', 'customerName'];
  const PHONE_PATTERN = /^((\+|0|00)?(420))?([67][0-9]{8})$/;
  const NAME_PATTERN = /^[\p{L} \p{M}.'-]+$/u;
  const EMAIL_PATTERN =
    /^(?=[a-z0-9@.!#$%&'*+/=?^_`{|}~-]{6,254}$)(?=[a-z0-9.!#$%&'*+/=?^_`{|}~-]{1,64}@)[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:(?=[a-z0-9-]{1,63}\.)[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?=[a-z0-9-]{1,63}$)[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
  const REQUEST_TIMEOUT_MS = 30000;
  const STORAGE_VERSION = 3;
  const RESULT_STEPS = ['pending', 'success', 'error'];
  const STOCK_RESULTS = {
    ITEM_NOT_STOCKED_AT_LOCATION: 'unavailable',
    ERROR: 'unavailable',
    ITEM_UNAVAILABLE: 'allUnavailable',
  };
  const STOCK_DESCRIPTIONS = {
    'Product is not stocked at the desired location but is available on others': 'unavailable',
    'Product is not available': 'allUnavailable',
  };

  const controllers = new Map();

  function normalize(field, value) {
    const trimmed = String(value || '').trim();

    return field === 'phoneNumber' ? trimmed.replace(/[\s-]/g, '') : trimmed;
  }

  function valid(field, value) {
    if (field === 'phoneNumber') return PHONE_PATTERN.test(value);
    if (field === 'customerEmail') return EMAIL_PATTERN.test(value);
    return NAME_PATTERN.test(value);
  }

  async function sendReservation(launcher, payload) {
    const url = new URL(launcher.dataset.endpoint, location.origin);

    if (url.origin !== location.origin || !launcher.dataset.endpoint) throw new Error('Invalid reservation proxy URL');

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });

    return { status: response.status, body: await response.json() };
  }

  function productFromFlow(flow) {
    return {
      id: flow.dataset.variantId,
      sapCode: flow.dataset.sapCode,
      title: flow.querySelector(SELECTORS.productTitle).textContent.trim(),
      image: flow.querySelector(SELECTORS.productImage)?.getAttribute('src') || '',
      fastReservationAllowed: flow.dataset.fastReservationAllowed === 'true',
    };
  }

  function storeFromItem(item) {
    return {
      id: item.dataset.pickupLocationId,
      siteCode: item.dataset.pickupSiteCode,
      name: item.dataset.pickupLocationName,
      address: item.dataset.pickupLocationAddress,
    };
  }

  function loadState(key, customerId) {
    try {
      const state = JSON.parse(sessionStorage.getItem(key));

      if (
        !state ||
        state.version !== STORAGE_VERSION ||
        state.customerId !== customerId ||
        !['stores', 'form', 'pending', 'success', 'error'].includes(state.step)
      )
        return null;

      if (!state.contacts || !FIELDS.every((field) => typeof state.contacts[field] === 'string')) return null;

      if (
        typeof state.search !== 'string' ||
        typeof state.open !== 'boolean' ||
        !Number.isInteger(state.attempts) ||
        state.attempts < 0
      )
        return null;

      if (state.step !== 'stores' && (!state.product?.id || !state.store?.id)) return null;

      if (state.store) {
        state.store.siteCode ||= state.store.storeId;
        delete state.store.storeId;
      }

      if (state.step === 'pending') {
        state.step = 'error';
        state.error = 'unknown';
      }

      return state;
    } catch {
      return null;
    }
  }

  class StoreReservation extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.key = `o2-reservation-v${STORAGE_VERSION}-${this.dataset.productId}`;
      this.customerId = this.dataset.customerId;
      this.customer = JSON.parse(this.querySelector(SELECTORS.customer).textContent);
      this.state = loadState(this.key, this.customerId);
      this.availabilityPending = true;
      this.inventory = new Map();

      controllers.set(this.dataset.productId, this);
      this.save();

      try {
        sessionStorage.removeItem(`o2-reservation-v1-${this.dataset.productId}`);
        sessionStorage.removeItem(`o2-reservation-v2-${this.dataset.productId}`);
      } catch {
        // Storage access can be unavailable; the in-memory state still works.
      }

      this.querySelector(SELECTORS.open).disabled = true;

      this.querySelector(SELECTORS.open).addEventListener('click', (event) => {
        if (this.canOpen()) this.drawer.show(event.currentTarget);
      });

      document.querySelectorAll(SELECTORS.flow).forEach((flow) => {
        if (flow.dataset.productId === this.dataset.productId && flow.isConnected) this.attach(flow);
      });
    }

    freshState() {
      return {
        version: STORAGE_VERSION,
        customerId: this.customerId,
        step: 'stores',
        contacts: { ...this.customer },
        search: '',
        open: false,
        attempts: 0,
        product: this.currentProduct,
        store: null,
      };
    }

    save() {
      try {
        if (this.state) sessionStorage.setItem(this.key, JSON.stringify(this.state));
        else sessionStorage.removeItem(this.key);
      } catch {
        // The in-memory flow remains usable when browser storage is unavailable.
      }
    }

    attach(flow) {
      if (this.flow === flow) return;
      if (flow.dataset.variantId !== this.dataset.variantId) return;

      if (!customElements.get('pickup-availability-drawer')) {
        customElements.whenDefined('pickup-availability-drawer').then(() => {
          if (flow.isConnected) this.attach(flow);
        });
        return;
      }

      this.flow = flow;
      this.drawer = flow.closest(SELECTORS.drawer);
      this.copy = JSON.parse(flow.querySelector(SELECTORS.copy).textContent);
      this.currentProduct = productFromFlow(flow);
      this.availabilityPending = flow.dataset.reservationFallback === 'true';
      this.refreshRun = (this.refreshRun || 0) + 1;
      this.inventory.set(
        this.currentProduct.id,
        [...flow.querySelectorAll(SELECTORS.locations)].map((item) => item.cloneNode(true))
      );

      if (this.state && ['stores', 'form'].includes(this.state.step)) {
        if (this.state.product?.id !== this.currentProduct.id || !this.currentProduct.fastReservationAllowed) {
          this.state.store = null;
          this.state.step = 'stores';
        }
        this.state.product = this.currentProduct;
      }

      this.syncLauncher();

      this.drawer.addEventListener(EVENTS.opening, () => {
        this.state ||= this.freshState();
        this.state.open = true;
        this.render();
        this.save();
      });

      this.drawer.addEventListener(EVENTS.closed, () => {
        if (this.state?.step === 'error') {
          this.state = this.freshState();
          this.render();
          this.save();
          this.refreshAvailability();
          return;
        }

        if (this.state && this.state.step !== 'success') this.state.open = false;
        else {
          this.state = null;
          this.refreshRun = (this.refreshRun || 0) + 1;
          this.refreshFailed = false;
        }
        this.save();
        this.syncLauncher();
      });

      flow.addEventListener('click', (event) => this.onClick(event));

      flow.querySelector(SELECTORS.form).addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });

      flow.querySelector(SELECTORS.form).addEventListener('focusout', (event) => {
        if (this.state && FIELDS.includes(event.target.name)) this.validateField(event.target.name);
      });

      flow.querySelector(SELECTORS.form).addEventListener('input', (event) => {
        if (!FIELDS.includes(event.target.name) || !this.state) return;

        this.state.contacts[event.target.name] = event.target.value;
        this.clearServerError(event.target.name);
        this.save();
      });

      flow.querySelector(SELECTORS.search).addEventListener('input', (event) => {
        if (!this.state) return;

        this.state.search = event.target.value;
        this.save();
      });

      flow.addEventListener(EVENTS.filtered, (event) => {
        const count = event.detail.count;
        const category = count === 1 ? 'one' : count >= 2 && count <= 4 ? 'few' : 'other';

        this.setText(SELECTORS.count, this.copy.counts[category].replace(/\d+/, count));
      });

      if (this.state) {
        this.render();
        this.save();

        if (this.state.open) this.drawer.show(this.querySelector(SELECTORS.open));
        if (this.state.step === 'error' && this.state.error !== 'allUnavailable') this.refreshAvailability();
      }
    }

    syncLauncher() {
      const entries = this.inventory.get(this.currentProduct?.id) || [];
      const button = this.querySelector(SELECTORS.open);
      const hasStock = this.currentProduct?.id === this.dataset.variantId && entries.length > 0;

      button.hidden = !hasStock || !this.currentProduct?.fastReservationAllowed;
      this.hidden = button.hidden;
      button.disabled = !this.canOpen();

      this.closest(SELECTORS.productInfo)
        ?.querySelectorAll(SELECTORS.pickupOpen)
        .forEach((link) => {
          link.hidden = !hasStock;
        });
    }

    canOpen() {
      return Boolean(this.flow?.isConnected && (!this.availabilityPending || RESULT_STEPS.includes(this.state?.step)));
    }

    availabilityFailed(availability) {
      this.availabilityPending = true;

      if (RESULT_STEPS.includes(this.state?.step) && !this.flow?.isConnected) {
        const drawer = this.querySelector(SELECTORS.fallback).content.firstElementChild.cloneNode(true);

        drawer.querySelector(SELECTORS.flow).dataset.reservationFallback = 'true';
        const colors = (availability.dataset.productPageColorScheme || '').split(' ').filter(Boolean);

        drawer.classList.add(...colors);
        document.body.appendChild(drawer);
      }
      this.syncLauncher();
    }

    variantChanged(variant) {
      this.dataset.variantId = variant?.id ? String(variant.id) : '';
      this.availabilityPending = true;
      this.syncLauncher();

      if (!this.state) return;

      if (RESULT_STEPS.includes(this.state.step)) {
        this.render();
        return;
      }
      this.state.step = 'stores';
      this.state.store = null;
      this.state.search = '';

      if (variant?.id) this.state.product = { ...this.state.product, id: String(variant.id) };

      this.drawer?.hide({ preserveState: true });
      this.save();
    }

    setText(selector, value) {
      this.flow.querySelectorAll(selector).forEach((element) => {
        element.textContent = value || '';
      });
    }

    toggle(selector, show) {
      this.flow.querySelectorAll(selector).forEach((element) => {
        element.hidden = !show;
      });
    }

    onClick(event) {
      if (!this.state || this.state.step === 'pending') return;
      if (!this.canOpen()) return;

      const target = event.target.closest(SELECTORS.button);

      if (!target) return;

      if (target.matches(SELECTORS.selectStore)) {
        if (!this.state.product.fastReservationAllowed) return;

        this.state.store = storeFromItem(target.closest(SELECTORS.locations));
        this.state.formOrigin = this.state.step;
        this.state.step = 'form';
        this.render(true);
      } else if (target.matches(SELECTORS.back)) {
        this.state.step = this.state.formOrigin || 'stores';
        this.render(true);
      } else if (target.matches(SELECTORS.changeStore)) {
        if (this.availabilityPending) return;

        if (target.matches(SELECTORS.changeSelectedStore)) {
          this.state.step = this.state.formOrigin || 'stores';
          this.render(true);
          this.save();
          return;
        }

        const previousVariant = this.state.product.id;

        this.state.step = 'stores';
        this.state.product = this.currentProduct;
        this.state.search = '';
        this.render(true);

        if (previousVariant !== this.currentProduct.id) this.refreshAvailability();
      } else if (target.matches(SELECTORS.retry)) {
        this.refreshAvailability();
      }
      this.save();
    }

    validateField(field) {
      this.state ||= this.freshState();
      const input = this.flow.querySelector(SELECTORS.field(field));
      const value = normalize(field, input.value);

      input.value = value;
      this.state.contacts[field] = value;
      const isValid = valid(field, value);

      this.state.fieldErrors ||= {};
      this.state.fieldErrors[field] = !isValid;
      input.setAttribute('aria-invalid', String(!isValid));
      this.toggle(SELECTORS.fieldError(field), !isValid);

      if (isValid) this.clearServerError(field);

      this.save();
      return isValid;
    }

    clearServerError(field) {
      this.state.validationKeys = (this.state.validationKeys || []).filter((key) => key !== field);
      this.state.serverValidation = this.state.validationKeys.length > 0;
      this.toggle(SELECTORS.validationError, this.state.step === 'form' && this.state.serverValidation);
    }

    async submit() {
      if (this.state?.step === 'pending') return;
      if (!this.canOpen()) return;
      if (!this.state?.product.fastReservationAllowed) return;

      const results = FIELDS.map((field) => this.validateField(field));

      if (results.includes(false)) {
        this.state.step = 'form';
        this.render();
        this.flow.querySelector(SELECTORS.invalidField)?.focus();
        return;
      }

      const sapText = String(this.state.product.sapCode || '').trim();
      const payload = {
        ...this.state.contacts,
        phoneNumber: `+420${this.state.contacts.phoneNumber.slice(-9)}`,
        locationId: String(this.state.store.id),
        siteCode: this.state.store.siteCode,
        storeName: this.state.store.name,
        sapCode: /^\d+$/.test(sapText) ? Number(sapText) : null,
      };

      if (
        !Number.isInteger(payload.sapCode) ||
        payload.sapCode < 0 ||
        payload.sapCode > 2147483647 ||
        !payload.siteCode
      ) {
        this.state.step = 'error';
        this.state.error = 'technical';
        this.render(true);
        this.save();
        this.refreshAvailability();
        return;
      }

      this.state.attempts += 1;
      this.refreshRun = (this.refreshRun || 0) + 1;
      this.refreshFailed = false;
      this.state.attemptId = crypto.randomUUID();
      const attemptId = this.state.attemptId;

      this.state.step = 'pending';
      this.state.serverValidation = false;
      this.state.validationKeys = [];
      this.render(true);
      this.save();

      try {
        const { status, body } = await sendReservation(this, payload);

        if (this.state?.attemptId !== attemptId) return;

        if (status === 200) {
          this.state.step = 'success';
          this.state.blockId = body.blockId || '';
          this.state.phone = payload.phoneNumber;
        } else if (status === 412) {
          this.state.step = 'form';
          this.state.validationKeys = (body.failures || [])
            .map((item) => item.key)
            .filter((key) => FIELDS.includes(key));
          this.state.serverValidation = true;
          this.state.formOrigin ||= 'stores';
        } else {
          this.state.step = 'error';
          this.state.error =
            status === 422
              ? STOCK_RESULTS[body.result] || STOCK_DESCRIPTIONS[body.resultDescription] || 'technical'
              : 'technical';
        }
      } catch {
        if (this.state?.attemptId !== attemptId) return;

        this.state.step = 'error';
        this.state.error = 'technical';
      }
      this.render(true);
      this.save();

      if (this.state.step === 'error' && this.state.error !== 'allUnavailable') this.refreshAvailability();
    }

    render(focus = false) {
      if (!this.state || !this.flow?.isConnected) return;

      const { step, product, store } = this.state;
      const entries = this.inventory.get(product?.id) || [];
      const hasAlternative = entries.some((item) => item.dataset.pickupLocationId !== store?.id);
      const allUnavailable = step === 'error' && this.state.error === 'allUnavailable';
      const reservationAllowed = product?.fastReservationAllowed;

      this.flow.querySelectorAll(SELECTORS.step).forEach((element) => {
        element.hidden = element.dataset.reservationStep !== step;
      });
      this.toggle(SELECTORS.product, ['stores', 'form', 'pending'].includes(step));
      this.toggle(SELECTORS.formActions, step === 'form');
      this.toggle(SELECTORS.benefits, reservationAllowed);
      this.toggle(SELECTORS.availabilityNotice, !reservationAllowed && step === 'stores');
      this.toggle(SELECTORS.changeSelectedStore, step === 'form' && reservationAllowed && hasAlternative);
      this.toggle(
        SELECTORS.chooseStore,
        step === 'error' && !allUnavailable && hasAlternative && !this.refreshFailed && !this.availabilityPending
      );
      this.toggle(
        SELECTORS.close,
        ['stores', 'success'].includes(step) ||
          allUnavailable ||
          (step === 'error' && !hasAlternative && !this.refreshFailed)
      );
      this.toggle(SELECTORS.retry, !allUnavailable && Boolean(this.refreshFailed));
      this.toggle(SELECTORS.alternatives, !allUnavailable);

      if (allUnavailable) {
        this.flow.querySelector(SELECTORS.alternatives).replaceChildren();
        this.toggle(SELECTORS.errorEmpty, false);
        this.toggle(SELECTORS.refreshing, false);
        this.toggle(SELECTORS.refreshError, false);
      }
      this.setText(SELECTORS.heading, reservationAllowed ? this.copy.title : this.copy.availabilityTitle);
      this.toggle(
        SELECTORS.subtitle,
        step === 'success' || step === 'form' || (step === 'stores' && entries.length > 0)
      );
      this.setText(
        SELECTORS.subtitle,
        step === 'stores'
          ? this.copy.ready
          : step === 'form'
            ? this.copy.contact
            : step === 'success'
              ? this.copy.success
              : ''
      );
      this.setText(SELECTORS.productTitle, product?.title || this.currentProduct.title);
      const media = this.flow.querySelector(SELECTORS.productMedia);
      const image = media?.querySelector(SELECTORS.productImage);

      if (media) {
        if (product?.image) {
          if (image) {
            if (image.getAttribute('src') !== product.image) {
              image.src = product.image;
              image.removeAttribute('srcset');
            }
          } else {
            const nextImage = document.createElement('img');

            nextImage.className = CLASSES.productImage;
            nextImage.src = product.image;
            nextImage.alt = '';
            media.replaceChildren(nextImage);
          }
        } else if (image) {
          media.replaceChildren(this.flow.querySelector(SELECTORS.productPlaceholder).content.cloneNode(true));
        }
      }
      this.setText(SELECTORS.storeName, store?.name);
      this.setText(SELECTORS.storeAddress, store?.address);
      this.setText(SELECTORS.number, this.state.blockId);
      this.setText(SELECTORS.sms, this.copy.sms.replace('__phone__', this.state.phone || ''));
      this.setText(SELECTORS.purchase, this.copy.purchase.replace('__number__', this.state.blockId || ''));

      FIELDS.forEach((field) => {
        this.flow.querySelector(SELECTORS.field(field)).value = this.state.contacts[field];
      });

      FIELDS.forEach((field) => {
        const invalid = Boolean(this.state.fieldErrors?.[field]);

        this.flow.querySelector(SELECTORS.field(field)).setAttribute('aria-invalid', String(invalid));
        this.toggle(SELECTORS.fieldError(field), invalid);
      });
      this.toggle(SELECTORS.validationError, step === 'form' && Boolean(this.state.serverValidation));

      for (const field of this.state.validationKeys || []) {
        this.flow.querySelector(SELECTORS.field(field)).setAttribute('aria-invalid', 'true');
        this.toggle(SELECTORS.fieldError(field), true);
      }
      this.setText(
        SELECTORS.errorTitle,
        this.copy[
          `${['unavailable', 'allUnavailable', 'unknown'].includes(this.state.error) ? this.state.error : 'error'}Title`
        ]
      );
      this.setText(
        SELECTORS.errorBody,
        this.copy[
          `${['unavailable', 'allUnavailable', 'unknown'].includes(this.state.error) ? this.state.error : 'error'}Body`
        ]
      );

      if (step === 'stores') this.renderList();

      if (focus && this.drawer.hasAttribute('open')) {
        const heading = this.flow.querySelector(SELECTORS.heading);

        trapFocus(this.drawer, heading);
        this.flow.querySelector(SELECTORS.body).scrollTop = 0;
      }
      this.syncLauncher();
    }

    renderList() {
      const list = this.flow.querySelector(SELECTORS.list);

      list.replaceChildren(...(this.inventory.get(this.currentProduct.id) || []).map((item) => item.cloneNode(true)));
      const search = this.flow.querySelector(SELECTORS.search);

      search.value = this.state?.search || '';
      window.PickupAvailabilityO2?.refreshSearch(this.drawer);
      const count = list.children.length;

      this.toggle(SELECTORS.empty, count === 0 && !this.refreshFailed);
    }

    async refreshAvailability() {
      if (!this.state || !this.flow?.isConnected) return;
      if (this.state.step === 'error' && this.state.error === 'allUnavailable') return;

      const variantId = this.state.step === 'stores' ? this.currentProduct.id : this.state.product.id;
      const run = (this.refreshRun || 0) + 1;

      this.refreshRun = run;
      this.refreshFailed = false;
      this.toggle(SELECTORS.refreshing, true);
      this.inventory.delete(String(variantId));
      this.toggle(SELECTORS.chooseStore, false);
      this.toggle(SELECTORS.refreshErrors, false);
      this.flow.querySelector(SELECTORS.alternatives).replaceChildren();
      const root = this.dataset.rootUrl.replace(/\/?$/, '/');

      try {
        const response = await fetch(`${root}variants/${variantId}/?section_id=pickup-availability-o2`, {
          cache: 'no-store',
          signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        });

        if (!response.ok) throw new Error(`Availability failed: ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextFlow = html.querySelector(SELECTORS.flow);

        if (!nextFlow || nextFlow.dataset.variantId !== String(variantId))
          throw new Error('Invalid availability response');

        if (this.refreshRun !== run || !this.state) return;
        if (String(variantId) === this.dataset.variantId) this.availabilityPending = false;

        const entries = [...nextFlow.querySelectorAll(SELECTORS.locations)];

        this.inventory.set(
          String(variantId),
          entries.map((item) => item.cloneNode(true))
        );
        const alternatives = entries.filter((item) => item.dataset.pickupLocationId !== this.state.store?.id);

        window.PickupAvailabilityO2?.sortLocations(alternatives);
        this.flow.querySelector(SELECTORS.alternatives).replaceChildren(...alternatives.slice(0, 2));

        if (this.state.step === 'error') {
          this.toggle(SELECTORS.errorEmpty, !entries.length);
          this.toggle(SELECTORS.chooseStore, alternatives.length > 0 && !this.availabilityPending);
          this.toggle(SELECTORS.close, !alternatives.length);
        }

        if (this.state.step === 'stores') this.render();

        if (this.drawer.hasAttribute('open')) {
          trapFocus(this.drawer, this.flow.querySelector(SELECTORS.heading));
        }
      } catch {
        if (this.refreshRun !== run || !this.state) return;

        this.refreshFailed = true;
        this.toggle(SELECTORS.refreshError, this.state.step === 'error');
        this.toggle(SELECTORS.listError, this.state.step === 'stores');
        this.toggle(SELECTORS.retry, true);
        this.toggle(SELECTORS.unavailableActions, false);
      } finally {
        if (this.refreshRun === run) {
          this.toggle(SELECTORS.refreshing, false);
          this.syncLauncher();
        }
      }
    }
  }

  window.StoreReservationO2 = {
    attachFlow(flow) {
      controllers.get(flow.dataset.productId)?.attach(flow);
    },
    canOpen(availability) {
      const launcher = availability.closest(SELECTORS.productInfo)?.querySelector(SELECTORS.launcher);

      return launcher ? launcher.canOpen() : true;
    },
    variantChanged(availability, variant) {
      const launcher = availability.closest(SELECTORS.productInfo)?.querySelector(SELECTORS.launcher);

      launcher?.variantChanged(variant);
    },
    availabilityFailed(availability) {
      const launcher = availability.closest(SELECTORS.productInfo)?.querySelector(SELECTORS.launcher);

      if (!launcher) return;

      launcher.availabilityFailed(availability);
    },
  };

  customElements.define(ELEMENT_NAME, StoreReservation);
})();
